import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { apiClient, useAuth } from "../../App";
import { 
  Hash, 
  Lock, 
  MessageSquare, 
  Users, 
  Plus, 
  ChevronDown, 
  ChevronRight,
  Search,
  Settings,
  RefreshCw
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { ScrollArea } from "../ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

const ChatSidebar = ({ channels, currentChannelId, onCreateChannel, onlineUsers, onRefresh }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDMDialog, setShowDMDialog] = useState(false);
  const [expandedSections, setExpandedSections] = useState({ channels: true, dms: true });
  const [searchQuery, setSearchQuery] = useState("");
  const [users, setUsers] = useState([]);
  
  // New channel form
  const [newChannel, setNewChannel] = useState({
    name: "",
    type: "public",
    description: ""
  });
  
  // DM form
  const [selectedUsers, setSelectedUsers] = useState([]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Filter channels
  const publicChannels = channels.filter(c => c.type === "public" || c.type === "program");
  const privateChannels = channels.filter(c => c.type === "private");
  const dmChannels = channels.filter(c => c.type === "dm");

  const filteredPublic = publicChannels.filter(c => 
    c.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const filteredPrivate = privateChannels.filter(c => 
    c.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const filteredDMs = dmChannels.filter(c => 
    c.dm_users?.some(u => u.name?.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Fetch users for DM creation
  const fetchUsers = async () => {
    try {
      const response = await apiClient.get("/users");
      setUsers(response.data.filter(u => u.id !== user?.id && u.status !== "invited"));
    } catch (e) {}
  };

  const handleCreateChannel = async () => {
    if (!newChannel.name.trim()) return;
    
    try {
      await onCreateChannel({
        name: newChannel.name,
        type: newChannel.type,
        description: newChannel.description
      });
      setShowCreateDialog(false);
      setNewChannel({ name: "", type: "public", description: "" });
    } catch (e) {}
  };

  const handleCreateDM = async () => {
    if (selectedUsers.length === 0) return;
    
    try {
      await onCreateChannel({
        type: "dm",
        name: "",
        member_ids: selectedUsers
      });
      setShowDMDialog(false);
      setSelectedUsers([]);
    } catch (e) {}
  };

  const getChannelIcon = (channel) => {
    switch (channel.type) {
      case "public": return <Hash className="w-4 h-4 text-muted-foreground flex-shrink-0" />;
      case "private": return <Lock className="w-4 h-4 text-muted-foreground flex-shrink-0" />;
      case "program": return <MessageSquare className="w-4 h-4 text-blue-500 flex-shrink-0" />;
      case "dm": return <Users className="w-4 h-4 text-muted-foreground flex-shrink-0" />;
      default: return <Hash className="w-4 h-4 text-muted-foreground flex-shrink-0" />;
    }
  };

  const isOnline = (userId) => onlineUsers.some(u => u.id === userId);

  return (
    <div className="w-64 border-r border-border flex flex-col bg-secondary/20" data-testid="chat-sidebar">
      {/* Header */}
      <div className="p-3 border-b border-border">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold">TRP Chat</h2>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onRefresh}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-8 h-8 text-sm"
          />
        </div>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-2">
          {/* Channels Section */}
          <div className="mb-4">
            <button
              onClick={() => toggleSection("channels")}
              className="flex items-center justify-between w-full px-2 py-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground"
            >
              <span className="flex items-center gap-1">
                {expandedSections.channels ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                Channels
              </span>
              <Button
                variant="ghost"
                size="icon"
                className="h-5 w-5"
                onClick={(e) => { e.stopPropagation(); setShowCreateDialog(true); }}
              >
                <Plus className="w-3 h-3" />
              </Button>
            </button>
            
            {expandedSections.channels && (
              <div className="space-y-0.5 mt-1">
                {filteredPublic.map((channel) => (
                  <Link
                    key={channel.id}
                    to={`/chat/${channel.id}`}
                    className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-sm transition-colors ${
                      channel.id === currentChannelId
                        ? "bg-primary text-primary-foreground"
                        : "hover:bg-secondary"
                    }`}
                    data-testid={`channel-${channel.id}`}
                  >
                    {getChannelIcon(channel)}
                    <span className="flex-1 truncate">{channel.name}</span>
                    {channel.unread_count > 0 && (
                      <Badge variant="destructive" className="h-5 min-w-[20px] justify-center rounded-full text-xs">
                        {channel.unread_count > 99 ? "99+" : channel.unread_count}
                      </Badge>
                    )}
                  </Link>
                ))}
                {filteredPrivate.map((channel) => (
                  <Link
                    key={channel.id}
                    to={`/chat/${channel.id}`}
                    className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-sm transition-colors ${
                      channel.id === currentChannelId
                        ? "bg-primary text-primary-foreground"
                        : "hover:bg-secondary"
                    }`}
                    data-testid={`channel-${channel.id}`}
                  >
                    {getChannelIcon(channel)}
                    <span className="flex-1 truncate">{channel.name}</span>
                    {channel.unread_count > 0 && (
                      <Badge variant="destructive" className="h-5 min-w-[20px] justify-center rounded-full text-xs">
                        {channel.unread_count}
                      </Badge>
                    )}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Direct Messages Section */}
          <div>
            <button
              onClick={() => toggleSection("dms")}
              className="flex items-center justify-between w-full px-2 py-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground"
            >
              <span className="flex items-center gap-1">
                {expandedSections.dms ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                Direct Messages
              </span>
              <Button
                variant="ghost"
                size="icon"
                className="h-5 w-5"
                onClick={(e) => { e.stopPropagation(); fetchUsers(); setShowDMDialog(true); }}
              >
                <Plus className="w-3 h-3" />
              </Button>
            </button>
            
            {expandedSections.dms && (
              <div className="space-y-0.5 mt-1">
                {filteredDMs.map((channel) => {
                  const otherUsers = channel.dm_users || [];
                  const displayName = otherUsers.map(u => u.name).join(", ") || "Unknown";
                  const anyOnline = otherUsers.some(u => isOnline(u.id));
                  
                  return (
                    <Link
                      key={channel.id}
                      to={`/chat/${channel.id}`}
                      className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-sm transition-colors ${
                        channel.id === currentChannelId
                          ? "bg-primary text-primary-foreground"
                          : "hover:bg-secondary"
                      }`}
                      data-testid={`dm-${channel.id}`}
                    >
                      <div className="relative">
                        <div className="w-6 h-6 rounded-full bg-secondary flex items-center justify-center text-xs font-medium">
                          {displayName.charAt(0).toUpperCase()}
                        </div>
                        {anyOnline && (
                          <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-green-500 border-2 border-background" />
                        )}
                      </div>
                      <span className="flex-1 truncate">{displayName}</span>
                      {channel.unread_count > 0 && (
                        <Badge variant="destructive" className="h-5 min-w-[20px] justify-center rounded-full text-xs">
                          {channel.unread_count}
                        </Badge>
                      )}
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </ScrollArea>

      {/* Create Channel Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Channel</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Channel Name</Label>
              <Input
                placeholder="e.g. general, design-team"
                value={newChannel.name}
                onChange={(e) => setNewChannel({ ...newChannel, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Type</Label>
              <Select
                value={newChannel.type}
                onValueChange={(value) => setNewChannel({ ...newChannel, type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="public">Public - Anyone can join</SelectItem>
                  <SelectItem value="private">Private - Invite only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Description (optional)</Label>
              <Input
                placeholder="What's this channel about?"
                value={newChannel.description}
                onChange={(e) => setNewChannel({ ...newChannel, description: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateChannel} disabled={!newChannel.name.trim()}>Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create DM Dialog */}
      <Dialog open={showDMDialog} onOpenChange={setShowDMDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Direct Message</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Select users</Label>
              <div className="max-h-64 overflow-y-auto space-y-1 border rounded-lg p-2">
                {users.map((u) => (
                  <button
                    key={u.id}
                    onClick={() => {
                      if (selectedUsers.includes(u.id)) {
                        setSelectedUsers(prev => prev.filter(id => id !== u.id));
                      } else {
                        setSelectedUsers(prev => [...prev, u.id]);
                      }
                    }}
                    className={`w-full flex items-center gap-2 p-2 rounded-md text-sm transition-colors ${
                      selectedUsers.includes(u.id)
                        ? "bg-primary text-primary-foreground"
                        : "hover:bg-secondary"
                    }`}
                  >
                    <div className="relative">
                      <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center text-xs font-medium">
                        {u.name?.charAt(0).toUpperCase()}
                      </div>
                      {isOnline(u.id) && (
                        <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-green-500 border-2 border-background" />
                      )}
                    </div>
                    <div className="flex-1 text-left">
                      <p className="font-medium">{u.name}</p>
                      <p className="text-xs opacity-70">{u.email}</p>
                    </div>
                  </button>
                ))}
              </div>
              {selectedUsers.length > 0 && (
                <p className="text-xs text-muted-foreground">{selectedUsers.length} user(s) selected</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDMDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateDM} disabled={selectedUsers.length === 0}>Start Conversation</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ChatSidebar;
